import path from 'path';

import { TriggersService, TriggerSourceType, TriggerTargetType } from '~features/triggers';
import { getService } from '~services/serviceFactory';
import SkopeiConnectService from '~services/skopeiConnectService';

import ScheduledActionTypeEnum from '../enums/scheduledActionTypeEnum';
import ScheduledAction from '../interfaces/scheduledAction';
import ScheduledActionException from '../interfaces/scheduledActionException';

class ScheduledActionsService extends SkopeiConnectService {
	protected readonly path = 'recurrence-schedules';

	/**
	 * FIXME:
	 * This call should be refactored, the service shouldn't be burdened with business logic.
	 * Even if there's some unexpected logic required because of the api structure, it doesn't guarantee
	 * it always needs to be done this way. Rather the domain should load the triggers and then the
	 * schedules after.
	 * This is to be changed on the backend first (supposedly). So we'll wait for that.
	 */
	async getScheduledActionsForDevice(
		params: {
			deviceId: string;
			deviceOrganisationId?: string;
			resourceGroupId?: string;
		},
		signal?: AbortSignal
	): Promise<ScheduledAction[]> {
		const triggerService = getService(TriggersService);
		const triggers = await triggerService.getTriggers(
			{
				pageNumber: 1,
				pageSize: 100,
				sourceType: TriggerSourceType.ScheduleTrigger,
				targetId: params.deviceId,
				targetType: TriggerTargetType.DeviceTarget,
				deviceOrganisationId: params.deviceOrganisationId,
			},
			signal
		);

		// Array from set to prevent duplicate ids.
		const scheduledActionsIds = Array.from(
			new Set(
				triggers.results
					.filter((trigger) => trigger.recurrenceScheduleId != undefined)
					.map((trigger) => trigger.recurrenceScheduleId)
			)
		);

		if (scheduledActionsIds.length === 0) {
			return [];
		}

		const queryParams = new URLSearchParams();
		scheduledActionsIds.forEach((id) => queryParams.append('id', String(id)));

		const endpoint = this.createEndpoint('v2');
		return await this.client
			.get<ScheduledActionResponse[]>(`${endpoint}?${queryParams.toString()}`, {
				signal: signal,
				params: {
					'resource-group-id': params.resourceGroupId,
				},
				headers: {
					OverwrittenClientId: params.deviceOrganisationId,
				},
			})
			.then((response) => {
				return Promise.resolve(response.data.map(ScheduledActionsService.fromResponse));
			});
	}

	public getScheduledActionsForDeviceCacheKey(
		source: string,
		deviceId?: string,
		overwrittenClientId?: string,
		resourceGroupId?: string
	) {
		return [this.path, source, deviceId, overwrittenClientId, resourceGroupId] as const;
	}

	/**
	 * Gets an array of Scheduled actions for an organisation.
	 *
	 * @param organisationId The organisation to get the scheduled actions for.
	 * @param resourceGroupId The resource group id to filter on.
	 * @param signal Signal for aborting.
	 *
	 * @returns an Array of scheduled actions.
	 *
	 */
	async getScheduledActionsForOrganisation(
		organisationId: string,
		resourceGroupId?: string,
		signal?: AbortSignal
	): Promise<ScheduledAction[]> {
		const endpoint = this.createEndpoint('v2');
		return await this.client
			.get<ScheduledActionResponse[]>(endpoint, {
				signal: signal,
				params: {
					'resource-group-id': resourceGroupId,
				},
				headers: {
					OverwrittenClientId: organisationId,
				},
			})
			.then((response) => {
				return response.data.map(ScheduledActionsService.fromResponse);
			});
	}

	/**
	 * Gets a scheduled action by it's id.
	 *
	 * @param scheduledActionId The id of the scheduled action.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns The scheduled action.
	 */
	async getScheduledActionById(
		scheduledActionId: string,
		organisationId?: string,
		signal?: AbortSignal
	): Promise<ScheduledAction> {
		const endpoint = this.createEndpoint('v2', [scheduledActionId]);
		return await this.client
			.get<ScheduledActionResponse>(endpoint, {
				signal: signal,
				headers: {
					OverwrittenClientId: organisationId,
				},
			})
			.then((response) => {
				return ScheduledActionsService.fromResponse(response.data);
			});
	}

	public getScheduledActionByIdCacheKey(scheduledActionId?: string, organisationId?: string) {
		return [this.path, scheduledActionId, organisationId] as const;
	}

	/**
	 * Gets the exceptions for a scheduled action.
	 *
	 * @param scheduledActionId The id of the scheduled action.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns The scheduled action.
	 */
	async getScheduledActionExceptions(
		scheduledActionId: string,
		organisationId?: string,
		signal?: AbortSignal
	): Promise<ScheduledActionException[]> {
		const endpoint = this.createEndpoint('v2', [scheduledActionId, 'exceptions']);
		return await this.client
			.get<ScheduledActionExceptionResponse[]>(endpoint, {
				signal: signal,
				headers: {
					OverwrittenClientId: organisationId,
				},
			})
			.then((response) => {
				return response.data.map(ScheduledActionsService.fromExceptionResponse);
			});
	}

	public getScheduledActionExceptionsCacheKey(scheduledActionId: string, organisationId?: string) {
		return [path.join(this.path, 'exceptions'), scheduledActionId, organisationId] as const;
	}

	/**
	 * Model parsing
	 */
	static fromResponse(response: ScheduledActionResponse): ScheduledAction {
		const {
			id,
			name,
			type,
			resourceGroupId,
			description,
			cronExpression,
			start,
			end,
			dateTime,
			...rest
		} = response;
		return {
			...rest,
			id: (id ?? 0).toString(),
			name: name,
			type: type as ScheduledActionTypeEnum,
			resourceGroupId: resourceGroupId?.toString(),
			description: description,
			cronExpression: cronExpression,
			start: new Date(start),
			end: end ? new Date(end) : undefined,
			dateTime: dateTime ? new Date(dateTime) : undefined,
		};
	}

	/**
	 * Model parsing
	 */
	static fromExceptionResponse(
		response: ScheduledActionExceptionResponse
	): ScheduledActionException {
		const { id, recurrenceScheduleId, dateTimeRange } = response;
		return {
			...response,
			id: (id ?? 0).toString(),
			recurrenceScheduleId: (recurrenceScheduleId ?? 0).toString(),
			start: new Date(dateTimeRange.start),
			end: new Date(dateTimeRange.end),
		};
	}
}

interface ScheduledActionResponse {
	type: string;
	resourceGroupId?: number;
	description?: string;
	cronExpression?: string;
	start: Date;
	end?: Date;
	dateTime?: Date;
	id: number;
	name: string;
}

interface ScheduledActionExceptionResponse {
	id: number;
	recurrenceScheduleId: number;
	dateTimeRange: {
		start: Date;
		end: Date;
	};
}

export default ScheduledActionsService;
